"use client";

import { usePathname } from "next/navigation";
import Tabs, { TAB_STYLE, TabItem } from "@/components/common/Tab";
import SubpageBackground from "@/components/common/SubpageBackground";
import PageTitle from "@/components/common/PageTitle";

// URLs that should skip the inner layout (only use outer layout with header/footer)
const SKIP_INNER_LAYOUT_URLS = [
  "/resources/security-advisories/trend_micro_server_protect_linux_command_injection",
  "/resources/security-advisories/oracle_EBS_local_file_inclusion",
  "/resources/security-advisories/microsoft_internet_explorer_heap_memory_corruption_vulnerability",
  "/resources/security-advisories/gordano_GMS_multiple_vulnerabilities",
  "/resources/security-advisories/602ProLanSuite_multiple_vulnerabilities",
  "/resources/security-advisories/mIRC_buffer_overflow_in_IRC_protocol",
  "/resources/security-advisories/TCLHttpd_multiple_vulnerabilities",
  "/resources/security-advisories/savant_web_Server_multiple_vulnerabilities",
  "/resources/security-advisories/FTGate_pro_multiple_vulnerabilities",
  "/resources/security-advisories/yahoo_messenger_multiple_vulnerabilities",
  "/resources/security-advisories/hosting_controller_multiple_vulnerabilities",
  "/resources/security-advisories/hosting_controller_ii_multiple_vulnerabilities",
];

export default function ResourceLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // Check if current path should skip inner layout
  const shouldSkipInnerLayout = SKIP_INNER_LAYOUT_URLS.some((url) => pathname.includes(url));

  const tabs: TabItem[] = [
    {
      id: "1",
      name: "Zero-day Tracker",
      value: "/resources/zero-day-tracker",
      href: "/resources/zero-day-tracker",
    },
    {
      id: "2",
      name: "Security Advisories",
      value: "/resources/security-advisories",
      href: "/resources/security-advisories",
    },
    {
      id: "3",
      name: "White Paper",
      value: "/resources/white-paper",
      href: "/resources/white-paper",
    },
  ];

  // Determine the title based on the current path
  const getTitle = () => {
    if (pathname.includes("zero-day-tracker")) return "Zero-day Tracker";
    if (pathname.includes("advisories")) return "Security Advisories";
    if (pathname.includes("white-paper")) return "White Paper";
    return "Resources";
  };

  return (
    // The bottom padding used to read `pb-[120px]-dark`, a typo Tailwind emits
    // no rule for, so /resources shipped with no bottom padding at all. Now it
    // matches the other page shells.
    <div className="relative min-h-screen flex flex-col bg-background pb-16 sm:pb-[120px]">
      {/* Decorative background moved to `next/image` to reduce CLS. */}
      {/* Resources uses the hexagon/tech pattern from Figma at 30% opacity. */}
      <SubpageBackground
        src="/images/subpage-background-tech.png"
        width={1548}
        height={1027}
        className="opacity-30"
      />

      {!shouldSkipInnerLayout ? (
        <div className="relative z-10 flex flex-col flex-1">
          {/* Title and Tab Section */}
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <section className="w-full max-w-[1300px] mx-auto pb-6 pt-[112px] md:pt-[128px] md:pb-[48px]">
              {/* Match the shared page-title spec used by /products, /service,
                  /company, /contact, /ot-security (Figma "SectionName", node
                  1531:101915): Avenir Next Bold 55px / 79px, 5px red accent bar.
                  Previously used BorderedHeading variant="headline-1", whose
                  token (48px / weight 600) overrode these classes and made the
                  Resources title inconsistent with every other tabbed page. */}
              {/* All four titles fit on one line at 390px — nothing to reserve. */}
              <PageTitle className="mb-4 md:mb-6">{getTitle()}</PageTitle>

              {/* Tab Navigation. First tab hugs the left margin (pl-0) so its
                  label lines up with the title accent bar — matching /products. */}
              <Tabs
                tabs={tabs}
                currentValue={pathname}
                style={TAB_STYLE.STYLE_1}
                showContent={false}
                useRouter={true}
                customClassName="[&>a:first-child]:!pl-0"
              />
            </section>
          </div>

          {/* Page Content */}
          <main className="flex-1 w-full">{children}</main>
        </div>
      ) : (
        <>{children}</>
      )}
    </div>
  );
}
